import {
    ActionType,
    AudienceId,
    BusinessProfileId,
    parseEntityIdSafe,
} from "@sp-crm/core";
import { AudienceShow } from "components/audience/audience-show";
import React from "react";
import { Redirect, Route, Switch } from "react-router";
import {
    useFeature,
    useIsAllowed,
    useIsAllowedInWholeTenant,
} from "store/selectors/hooks";
import { businessVerificationPath } from "./business-verification/business-profile-types";
import { BusinessVerificationPage } from "./business-verification/business-verification-page";
import { BusinessVerificationWizard } from "./business-verification/business-verification-wizard";
import { ManageIntegrationAccounts } from "./manage-integration-accounts";
import { ManageIntegrationsAxisCare } from "./manage-integrations-axiscare";
import { ManageIntegrationsConstantContact } from "./manage-integrations-constant-contact";
import { ManageIntegrationsMailchimp } from "./manage-integrations-mailchimp";
import { ManageIntegrationsNav } from "./manage-integrations-nav";
import { ManageIntegrationsQuickBooks } from "./manage-integrations-quick-books";
import { ManageIntegrationsTwilio } from "./manage-integrations-twilio";
import { ManageIntegrationsWellSky } from "./manage-integrations-wellsky";
import { ManageWebhookDetail } from "./manage-webhook-detail";
import { ManageWebhooks } from "./manage-webhooks";

export const ManageIntegrations: React.FC<unknown> = () => {
    const manageIntegrationsAllowed = useIsAllowed(ActionType.ManageIntegrations);
    const wholeTenantAccess = useIsAllowedInWholeTenant(ActionType.ManageIntegrations);
    const isPlacementEnabled = useFeature("placement");
    const isWellSkyEnabled = useFeature("wellSkyIntegration");
    const isHomeCareEnabled = useFeature("homeCarePrototype");
    const isBusinessVerificationEnabled = useFeature("businessVerification");
    const defaultRoute = wholeTenantAccess
        ? "/settings/integrations/accounts"
        : isPlacementEnabled
          ? "/settings/integrations/quickbooks"
          : "/settings/integrations/accounts";
    if (!manageIntegrationsAllowed) {
        return null;
    }
    return (
        <div className="lg:flex lg:gap-x-5">
            <div className="lg:w-56 lg:shrink-0">
                <ManageIntegrationsNav />
            </div>
            <div className="lg:flex-1 lg:min-w-0 pb-4">
                <Switch>
                    <Route
                        path="/settings/integrations/accounts"
                        component={ManageIntegrationAccounts}
                    />
                    {isPlacementEnabled && (
                        <Route
                            path="/settings/integrations/quickbooks"
                            component={ManageIntegrationsQuickBooks}
                        />
                    )}
                    {isPlacementEnabled && (
                        <Route
                            path="/settings/integrations/webhooks/:webhookId"
                            component={ManageWebhookDetail}
                        />
                    )}
                    {isPlacementEnabled && (
                        <Route
                            path="/settings/integrations/webhooks"
                            component={ManageWebhooks}
                        />
                    )}
                    {isPlacementEnabled && (
                        <Route
                            path="/settings/integrations/mailchimp/audiences/:audienceId/:subtab?"
                            render={props => {
                                const audienceIdParam = props.match.params.audienceId;
                                const subtab = props.match.params.subtab || "recipients";
                                const audienceId =
                                    parseEntityIdSafe<AudienceId>(
                                        audienceIdParam,
                                    ).getOrElse(null);

                                if (!audienceId) {
                                    return (
                                        <Redirect to="/settings/integrations/mailchimp" />
                                    );
                                }

                                return (
                                    <AudienceShow
                                        audienceId={audienceId}
                                        subtab={subtab}
                                    />
                                );
                            }}
                        />
                    )}
                    {isPlacementEnabled && (
                        <Route
                            path="/settings/integrations/mailchimp"
                            render={props => (
                                <ManageIntegrationsMailchimp
                                    match={{
                                        path: props.match.path,
                                        params: {
                                            subtab: "audiences",
                                        },
                                    }}
                                />
                            )}
                        />
                    )}
                    {isPlacementEnabled && (
                        <Route
                            path="/settings/integrations/constant-contact/audiences/:audienceId/:subtab?"
                            render={props => {
                                const audienceIdParam = props.match.params.audienceId;
                                const subtab = props.match.params.subtab || "recipients";
                                const audienceId =
                                    parseEntityIdSafe<AudienceId>(
                                        audienceIdParam,
                                    ).getOrElse(null);

                                if (!audienceId) {
                                    return (
                                        <Redirect to="/settings/integrations/constant-contact" />
                                    );
                                }

                                return (
                                    <AudienceShow
                                        audienceId={audienceId}
                                        subtab={subtab}
                                    />
                                );
                            }}
                        />
                    )}
                    {isPlacementEnabled && (
                        <Route
                            path="/settings/integrations/constant-contact"
                            render={props => (
                                <ManageIntegrationsConstantContact
                                    match={{
                                        path: props.match.path,
                                        params: {
                                            subtab: "audiences",
                                        },
                                    }}
                                />
                            )}
                        />
                    )}
                    {isPlacementEnabled && (
                        <Route
                            path="/settings/integrations/twilio"
                            component={ManageIntegrationsTwilio}
                        />
                    )}
                    {isBusinessVerificationEnabled && (
                        <Route
                            path={`${businessVerificationPath}/:businessProfileId`}
                            render={props => {
                                const businessProfileId =
                                    parseEntityIdSafe<BusinessProfileId>(
                                        props.match.params.businessProfileId,
                                    ).getOrElse(null);

                                if (!businessProfileId) {
                                    return <Redirect to={businessVerificationPath} />;
                                }

                                return (
                                    <BusinessVerificationWizard
                                        businessProfileId={businessProfileId}
                                    />
                                );
                            }}
                        />
                    )}
                    {isBusinessVerificationEnabled && (
                        <Route
                            path={businessVerificationPath}
                            component={BusinessVerificationPage}
                        />
                    )}
                    {isWellSkyEnabled && (
                        <Route
                            path="/settings/integrations/wellsky-personal-care"
                            component={ManageIntegrationsWellSky}
                        />
                    )}
                    {isHomeCareEnabled && (
                        <Route
                            path="/settings/integrations/axiscare"
                            component={ManageIntegrationsAxisCare}
                        />
                    )}
                    <Route
                        path="/settings/integrations"
                        render={() => <Redirect to={defaultRoute} />}
                    />
                </Switch>
            </div>
        </div>
    );
};
